import React, {useEffect, useCallback, useReducer, useRef, useState, useMemo } from 'react';
import Lobby from './components/Lobby';
import WaitingRoom from './components/waiting-room/WaitingRoom';
import GameController from './components/GameController';
import NetworkGate from './components/NetworkGate';
import CategorySelection from './components/category/CategorySelection';
import ParticleBackground from './components/common/ParticleBackground';
import CyberLoader from './components/common/CyberLoader';
import TauntOverlay from './components/common/TauntOverlay';
import BrandFooter from './components/common/BrandFooter';
import JoinTableSplash from './components/common/JoinTableSplash';
import AuthScreen from './components/auth/AuthScreen';
import Dashboard from './components/dashboard/Dashboard';
import Leaderboard from './components/dashboard/Leaderboard';
import AdminDashboard from './components/admin/AdminDashboard';
import { motion, AnimatePresence } from 'motion/react';
import './index.css';
import './App.css';

const initialState = {
  connected: false,
  roomId: null,
  myPlayerId: null,
  gameState: null,
  error: null,
  isHostedMode: true,
  showDashboard: false,
  showLeaderboard: false,
  showAdminDashboard: false,
  trickNotif: null,
  tunnelUrl: '', 
};

function reducer(state, action) {
  switch (action.type) {
    case 'CONNECTED':    return { ...state, connected: true };
    case 'DISCONNECTED': return { ...state, connected: false };
    case 'GAME_STATE':   return { ...state, gameState: action.payload, error: null };
    case 'ROOM_CREATED':
    case 'JOINED_ROOM':
      return { 
        ...state, 
        roomId: action.payload.room_id, 
        myPlayerId: action.payload.player_id,
        myToken: action.payload.token 
      };
    case 'RECONNECTED':  return { ...state, roomId: action.payload.room_id, myPlayerId: action.payload.player_id };
    case 'SET_TUNNEL_URL': return { ...state, tunnelUrl: action.payload };
    case 'LEAVE_ROOM':   return { ...initialState, connected: state.connected, tunnelUrl: state.tunnelUrl };
    case 'ERROR':        return { ...state, error: action.payload.message };
    case 'CLEAR_ERROR':  return { ...state, error: null };
    case 'SHOW_DASHBOARD': return { ...state, showDashboard: action.payload };
    case 'SHOW_LEADERBOARD': return { ...state, showLeaderboard: action.payload };
    case 'SHOW_ADMIN_DASHBOARD': return { ...state, showAdminDashboard: action.payload };
    case 'TRICK_NOTIF':  return { ...state, trickNotif: action.payload };
    case 'CLEAR_NOTIF':  return { ...state, trickNotif: null };
    default: return state;
  }
}

export default function App() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [networkMode, setNetworkMode] = useState(null);
  const [category, setCategory] = useState(null);
  const [lastPrank, setLastPrank] = useState(null);
  const [hasJoinedTable, setHasJoinedTable] = useState(false);
  const [authToken, setAuthToken] = useState(() => {
    const token = localStorage.getItem('vault_jwt');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp * 1000 < Date.now()) {
          localStorage.removeItem('vault_jwt');
          localStorage.removeItem('vault_username');
          return null;
        }
      } catch (e) {}
    }
    return token;
  });
  const [authUsername, setAuthUsername] = useState(() => localStorage.getItem('vault_username'));
  const [isAdmin, setIsAdmin] = useState(false);
  const wsRef = useRef(null);
  const notifTimer = useRef(null);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!authToken) return;
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      try {
        const resp = await fetch(`${baseUrl}/api/admin/invite-code`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (resp.ok) {
          setIsAdmin(true);
        }
      } catch (e) {}
    };
    checkAdminStatus();
  }, [authToken]);

  const send = useCallback((type, payload) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type, payload: payload ?? {} }));
    }
  }, []);

  const { 
    connected, roomId, myPlayerId, gameState, error, trickNotif, tunnelUrl: localTunnelUrl, 
    showDashboard, showLeaderboard, showAdminDashboard 
  } = state;
  const tunnelUrl = gameState?.tunnel_url || localTunnelUrl;

  const handleJoinTable = () => {
    console.log("🔊 [Audio] Unlocking audio context via user gesture...");
    setHasJoinedTable(true);
    if (navigator.vibrate) navigator.vibrate(50);
  };

  const isMobile = useMemo(() => {
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth < 1024;
  }, []);

  useEffect(() => {
    if (!roomId) setHasJoinedTable(false);
  }, [roomId]);

  useEffect(() => {
    if (!isMobile && roomId && !hasJoinedTable) {
      setHasJoinedTable(true);
    }
  }, [isMobile, roomId, hasJoinedTable]);

  const isSpectator = gameState?.players && !gameState.players.some(p => p && p.id === myPlayerId);

  const isLocal = window.location.hostname === 'localhost' || 
                  window.location.hostname === '127.0.0.1' || 
                  window.location.hostname.startsWith('192.168.') ||
                  window.location.hostname.endsWith('.local');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const roomFromUrl = params.get('room');
    if (roomFromUrl) {
      setNetworkMode('online');
      setCategory('cards');
    }
  }, []);

  useEffect(() => {
    if (!isLocal && !localTunnelUrl) {
      const currentUrl = window.location.origin;
      if (currentUrl.includes('trycloudflare.com')) {
        dispatch({ type: 'SET_TUNNEL_URL', payload: currentUrl });
      }
    }
  }, [isLocal, localTunnelUrl]);

  useEffect(() => {
    const interval = setInterval(() => {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      fetch(`${baseUrl}/api/heartbeat`).catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (process.env.REACT_APP_BACKEND_URL) return;

    if (isLocal && networkMode === 'online' && !tunnelUrl) {
      let pollTimer;
      const fetchTunnel = async () => {
        try {
          const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
          const resp = await fetch(`${baseUrl}/api/tunnel/start`, { method: 'POST' });
          if (!resp.ok) return;
          const data = await resp.json();
          if (data.status === 'ready' && data.url) {
            dispatch({ type: 'SET_TUNNEL_URL', payload: data.url });
            clearInterval(pollTimer);
          }
        } catch (e) { }
      };
      fetchTunnel();
      pollTimer = setInterval(fetchTunnel, 4000);
      return () => clearInterval(pollTimer);
    }
  }, [isLocal, networkMode, tunnelUrl]);

  useEffect(() => {
    if (isLocal && localTunnelUrl && roomId) {
      send('update_tunnel_url', { url: localTunnelUrl });
    }
  }, [isLocal, localTunnelUrl, roomId, send]);

  useEffect(() => {
    if (!isLocal && !networkMode) {
      setNetworkMode('online');
      setCategory('cards');
    }
  }, [isLocal, networkMode]);

  useEffect(() => {
    let socket = null;
    let reconnectTimer = null;

    if (process.env.REACT_APP_BACKEND_URL && !authToken) return;

    if (authToken) {
      try {
        const payload = JSON.parse(atob(authToken.split('.')[1]));
        if (payload.exp * 1000 < Date.now()) {
          setAuthToken(null);
          localStorage.removeItem('vault_jwt');
          localStorage.removeItem('vault_username');
          return;
        }
      } catch(e) {}
    }

    const connect = () => {
      let backendHost = window.location.host;
      let protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      
      if (process.env.REACT_APP_BACKEND_URL) {
        try {
          const urlObj = new URL(process.env.REACT_APP_BACKEND_URL);
          backendHost = urlObj.host;
          protocol = urlObj.protocol === 'https:' ? 'wss:' : 'ws:';
        } catch(e) {}
      }

      let wsUrl = `${protocol}//${backendHost}/ws`;
      if (process.env.REACT_APP_BACKEND_URL && authToken) {
        wsUrl += `?token=${authToken}`;
      }

      socket = new WebSocket(wsUrl);
      wsRef.current = socket;

      socket.onopen = () => {
        dispatch({ type: 'CONNECTED' });
        try {
          const savedRoom = localStorage.getItem('game28_room');
          const savedToken = localStorage.getItem('game28_token');
          if (savedRoom && savedToken) {
            socket.send(JSON.stringify({
              type: 'reconnect',
              payload: { room_id: savedRoom, token: savedToken }
            }));
          }
        } catch (e) { }
      };

      socket.onclose = () => {
        dispatch({ type: 'DISCONNECTED' });
        reconnectTimer = setTimeout(() => connect(), 3000);
      };

      socket.onerror = () => {
        dispatch({ type: 'DISCONNECTED' });
      };

      socket.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          switch (msg.type) {
            case 'game_state':
              dispatch({ type: 'GAME_STATE', payload: msg.payload });
              break;
            case 'room_created':
            case 'joined_room':
            case 'seat_claimed':
              dispatch({ type: msg.type === 'room_created' ? 'ROOM_CREATED' : 'JOINED_ROOM', payload: msg.payload });
              if (msg.type === 'room_created' && localTunnelUrl) {
                send('update_tunnel_url', { url: localTunnelUrl });
              }
              try {
                localStorage.setItem('game28_room', msg.payload.room_id);
                localStorage.setItem('game28_token', msg.payload.token);
              } catch(e) {}
              break;
            case 'reconnected':
              dispatch({ type: 'RECONNECTED', payload: msg.payload });
              setCategory('cards');
              break;
            case 'left_room': 
            case 'kicked':
              try {
                localStorage.removeItem('game28_room');
                localStorage.removeItem('game28_token');
                if (window.history.replaceState) {
                  const url = new URL(window.location.href);
                  url.searchParams.delete('room');
                  window.history.replaceState({}, '', url.toString());
                }
              } catch(e) {}
              if (msg.type === 'kicked') {
                dispatch({ type: 'ERROR', payload: msg.payload });
              }
              dispatch({ type: 'LEAVE_ROOM' });
              break;
            case 'trick_complete':
              dispatch({ type: 'TRICK_NOTIF', payload: msg.payload });
              if (notifTimer.current) clearTimeout(notifTimer.current);
              notifTimer.current = setTimeout(() => dispatch({ type: 'CLEAR_NOTIF' }), 2500);
              break;
            case 'error':
              if (msg.payload && msg.payload.message && msg.payload.message.includes('not found')) {
                 localStorage.removeItem('game28_room');
                 localStorage.removeItem('game28_token');
              }
              dispatch({ type: 'ERROR', payload: msg.payload });
              break;
            case 'prank':
              setLastPrank({ ...msg.payload, timestamp: Date.now() });
              break;
            default: break;
          }
        } catch (e) { }
      };
    };

    connect();

    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (socket) {
        socket.onclose = null;
        socket.close();
      }
      if (notifTimer.current) clearTimeout(notifTimer.current);
    };
  }, [send, localTunnelUrl, authToken]); 

  const handleAuthSuccess = (token, username) => {
    setAuthToken(token);
    setAuthUsername(username);
  };

  const isHostedMode = !!process.env.REACT_APP_BACKEND_URL;

  if (isHostedMode && !authToken) {
    return <AuthScreen onLogin={handleAuthSuccess} />;
  }

  let mainContent;

  if (showDashboard) {
    mainContent = <Dashboard onBack={() => dispatch({ type: 'SHOW_DASHBOARD', payload: false })} />;
  } else if (showLeaderboard) {
    mainContent = <Leaderboard onBack={() => dispatch({ type: 'SHOW_LEADERBOARD', payload: false })} />;
  } else if (showAdminDashboard) {
    mainContent = <AdminDashboard onBack={() => dispatch({ type: 'SHOW_ADMIN_DASHBOARD', payload: false })} />;
  } else if (!connected) {
    return (
      <div className="app-layout">
        <div className="splash">
          <ParticleBackground />
          <motion.div 
            className="splash-card"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <motion.div 
              className="splash-logo-wrapper"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            >
              <img src="/assets/resources/The-Game-Vault.svg" alt="The Game Vault" className="splash-main-logo" />
            </motion.div>
            <p className="splash-subtitle">Connecting to secure server…</p>
            <CyberLoader />
          </motion.div>
        </div>
        <BrandFooter />
      </div>
    );
  } else if (isLocal && !networkMode) {
    mainContent = <NetworkGate onSelect={setNetworkMode} />;
  }
  else if (isLocal && !category && !roomId) {
    mainContent = <CategorySelection onSelect={setCategory} />;
  }
  else if (!roomId) {
    mainContent = (
      <Lobby
        send={send}
        error={error}
        clearError={() => dispatch({ type: 'CLEAR_ERROR' })}
        onBack={() => setCategory(null)}
        tunnelUrl={tunnelUrl}
        category={category}
        authUsername={authUsername}
      />
    );
  } else {
    const isWaiting = !gameState || gameState.phase === 'waiting' || gameState.status === 'waiting';

    if (isWaiting) {
      mainContent = (
        <WaitingRoom
          gameState={gameState}
          roomId={roomId}
          myPlayerId={myPlayerId}
          send={send}
          error={error}
          clearError={() => dispatch({ type: 'CLEAR_ERROR' })}
          tunnelUrl={tunnelUrl}
          isSpectator={isSpectator}
          networkMode={networkMode}
        />
      );
    } else {
      mainContent = (
        <GameController
          gameState={gameState}
          roomId={roomId}
          myPlayerId={myPlayerId}
          send={send}
          error={error}
          trickNotif={trickNotif}
          tunnelUrl={tunnelUrl}
          clearError={() => dispatch({ type: 'CLEAR_ERROR' })}
          onLeave={() => send('leave_room')}
          isSpectator={isSpectator}
        />
      );
    }
  }

  return (
    <div className="app-layout">
      {mainContent}
      
      <AnimatePresence>
        {roomId && !hasJoinedTable && (
          <JoinTableSplash 
            onJoin={handleJoinTable} 
            roomMode={gameState?.mode} 
          />
        )}
      </AnimatePresence>

      {isHostedMode && !roomId && !showDashboard && !showLeaderboard && !showAdminDashboard && (
        <div style={{ position: 'absolute', top: '24px', right: '24px', zIndex: 10, display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <button 
            className="btn btn-primary" 
            onClick={() => dispatch({ type: 'SHOW_DASHBOARD', payload: true })}
            style={{ padding: '12px 24px', width: 'auto' }}
          >
            {authUsername || 'Player'}'s Profile
          </button>
          <button 
            className="btn btn-primary" 
            onClick={() => dispatch({ type: 'SHOW_LEADERBOARD', payload: true })}
            style={{ padding: '12px 24px', width: 'auto' }}
          >
            Leaderboard
          </button>
          {isAdmin && (
            <button 
              className="btn btn-primary" 
              onClick={() => dispatch({ type: 'SHOW_ADMIN_DASHBOARD', payload: true })}
              style={{ padding: '12px 24px', width: 'auto' }}
            >
              Admin Dashboard
            </button>
          )}
        </div>
      )}

      <TauntOverlay lastPrank={lastPrank} />
      <BrandFooter />
    </div>
  );
}
